/**
 * Google Analytics 4 integration.
 *
 * - SSR-safe: all browser access is guarded and deferred to the client.
 * - Only loads in production when VITE_GA_MEASUREMENT_ID is set.
 * - Manual page_view tracking (send_page_view is disabled during config) so
 *   client-side navigation with TanStack Router doesn't fire duplicates.
 */

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const IS_PROD = import.meta.env.PROD;

let initialized = false;

function isEnabled(): boolean {
  return Boolean(IS_PROD && MEASUREMENT_ID && typeof window !== "undefined");
}

/** Load gtag.js once. Safe to call multiple times. */
export function initAnalytics(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  const gtag: GtagFn = function (...args) {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  gtag("js", new Date());
  // Disable auto page_view — we send them manually on route resolve.
  gtag("config", MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export function trackPageView(path: string, title?: string): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
    send_to: MEASUREMENT_ID,
  });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag("event", name, params);
}

export function trackAffiliateClick(params: {
  gameId?: string | null;
  gameName?: string | null;
  shopName?: string | null;
  platform?: string | null;
  price?: number | null;
  currency?: string | null;
  url?: string | null;
}): void {
  trackEvent("affiliate_click", {
    game_id: params.gameId ?? undefined,
    game_name: params.gameName ?? undefined,
    shop_name: params.shopName ?? undefined,
    platform: params.platform ?? undefined,
    price: params.price ?? undefined,
    currency: params.currency ?? undefined,
    link_url: params.url ?? undefined,
  });
}

export function trackSearch(query: string): void {
  const q = query.trim();
  if (!q) return;
  trackEvent("search", { search_term: q });
}

export function trackWishlist(action: "add" | "remove", gameId: string): void {
  trackEvent(action === "add" ? "add_to_wishlist" : "remove_from_wishlist", {
    game_id: gameId,
  });
}
